import { useEffect, useRef, useState } from 'react';
import { Link, useSearch } from 'wouter';

import { listWorkspaces, openWorkspace } from '../../api/workspaces';
import { openFailureMessage, openWithWake, WAKING_MESSAGE } from './WorkspacesPanel';

/** The host is only a lookup hint; the signed-in workspace list remains the authority. */
export function workspaceHostHint(search: string): string {
  return new URLSearchParams(search).get('workspace_host')?.trim().toLowerCase() || '';
}

export function WakeWorkspaceView({ loading, waking, error, canRetry, onRetry }: {
  loading: boolean; waking: boolean; error: string; canRetry: boolean; onRetry: () => void;
}) {
  return (
    <main className="route-state" aria-labelledby="wake-heading">
      <p className="eyebrow">Your studio</p>
      <h1 id="wake-heading">{waking ? 'Waking your studio…' : loading ? 'Checking your studio…' : 'Your studio needs attention'}</h1>
      {waking ? <p role="status" aria-live="polite">{WAKING_MESSAGE}</p> : null}
      {loading ? <p role="status" aria-live="polite">Checking that this studio belongs to your account…</p> : null}
      {error ? <p role="alert">{error}</p> : null}
      {canRetry ? <button type="button" onClick={onRetry}>Try again</button> : null}
      {!waking && !loading ? <p><Link to="/support">Report a problem</Link> · <Link to="/projects">Your account</Link></p> : null}
    </main>
  );
}

export function WakeWorkspacePage() {
  const search = useSearch();
  const host = workspaceHostHint(search);
  const [loading, setLoading] = useState(true);
  const [waking, setWaking] = useState(false);
  const [error, setError] = useState('');
  const active = useRef(true);
  const busy = useRef(false);

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  async function wake() {
    if (busy.current) return;
    busy.current = true;
    setLoading(true);
    setWaking(false);
    setError('');
    try {
      const rows = await listWorkspaces();
      if (!active.current) return;
      if (!host) throw new Error('The studio address is missing. Return to your studio list and open it again.');
      const workspace = rows.find((row) => row.ingress_host.toLowerCase() === host);
      if (!workspace) throw new Error('This studio is not available to your account. Sign in with the account that owns it.');
      if (workspace.state !== 'sleeping' && workspace.state !== 'active') {
        throw new Error('This studio cannot be woken right now. Check Billing or contact support.');
      }
      setLoading(false);
      await openWithWake(workspace, {
        open: (row) => openWorkspace(row.workspace_id, row.ingress_host, undefined, () => active.current),
        list: listWorkspaces,
        onWaking: () => { if (active.current) setWaking(true); },
        wait: (ms) => new Promise((resolve) => globalThis.setTimeout(resolve, ms)),
        now: () => Date.now(),
      });
    } catch (failure) {
      if (active.current) {
        setError(failure instanceof Error && failure.name !== 'ApiClientError'
          ? failure.message : openFailureMessage(failure));
        setWaking(false);
        setLoading(false);
      }
    } finally {
      busy.current = false;
    }
  }

  useEffect(() => { void wake(); }, [host]);

  return <WakeWorkspaceView loading={loading} waking={waking} error={error} canRetry={!loading && !waking} onRetry={() => void wake()} />;
}
